class HanroPdpSwatches extends HTMLElement {
  #variantPicker = null;
  #initFrame = null;

  connectedCallback() {
    // Variant picker block renders after this block in the DOM,
    // so defer initialization until all sibling blocks are parsed.
    this.#initFrame = requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    if (this.#initFrame) cancelAnimationFrame(this.#initFrame);
    if (this.#variantPicker) {
      this.#variantPicker.removeEventListener('change', this.#handleVariantChange);
    }
  }

  #init() {
    const section = this.closest('.shopify-section');
    this.#variantPicker = section?.querySelector('variant-picker');

    if (this.#variantPicker) {
      this.#variantPicker.addEventListener('change', this.#handleVariantChange);
      // Run stock sync for initially selected size
      this.#handleVariantChange();
    }

    // Intercept swatch clicks to append ?variant= for size persistence
    this.addEventListener('click', this.#handleSwatchClick);
  }

  /**
   * When a swatch link is clicked, find the variant on the target product
   * that matches the currently selected size and navigate with ?variant=ID.
   * This lets Shopify server-render the correct size — no flash.
   */
  #handleSwatchClick = (event) => {
    const swatch = event.target.closest('.sibling-swatch');
    if (!swatch || swatch.classList.contains('active')) return;

    const selectedSize = this.#getSelectedSize();
    if (!selectedSize) return;

    try {
      const variants = JSON.parse(swatch.dataset.variants || '[]');
      const match = variants.find((v) => v.option === selectedSize);

      if (match?.id) {
        event.preventDefault();
        const url = new URL(swatch.href, window.location.origin);
        url.searchParams.set('variant', match.id);
        window.location.href = url.toString();
      }
    } catch {
      // Invalid JSON — let the default link navigate normally
    }
  };

  #handleVariantChange = () => {
    const selectedSize = this.#getSelectedSize();
    if (!selectedSize) return;

    this.#updateSwatchAvailability(selectedSize);
  };

  /**
   * Gets the selected value from the first visible option fieldset.
   * Skips hidden fieldsets (e.g., single-value Color option hidden by sibling swatches block).
   */
  #getSelectedSize() {
    if (!this.#variantPicker) return null;

    for (const fieldset of this.#variantPicker.querySelectorAll('fieldset')) {
      if (!fieldset.offsetHeight) continue;

      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value;

      const select = fieldset.querySelector('select');
      if (select) return select.value;
    }
    return null;
  }

  #updateSwatchAvailability(selectedSize) {
    this.querySelectorAll('.sibling-swatch').forEach((swatch) => {
      try {
        const variants = JSON.parse(swatch.dataset.variants || '[]');
        const match = variants.find((v) => v.option === selectedSize);

        if (!match || !match.available) {
          swatch.classList.add('sibling-swatch--size-unavailable');
        } else {
          swatch.classList.remove('sibling-swatch--size-unavailable');
        }
      } catch (e) {
        // Invalid JSON — skip this swatch
      }
    });
  }
}

customElements.define('hanro-pdp-swatches', HanroPdpSwatches);
